/** In-app notifications (FR-NOTIF). The text is written by the server; the target carries ids only. */
import { z } from "zod";
export const NotificationKind = z.enum([
    "result_recorded",
    "result_corrected",
    "recitation_requested",
    "exam_scheduled",
    "exam_approved",
    "plan_approved",
    "appointment_scheduled",
    "appointment_cancelled",
    "plan_proposed",
    "announcement",
    "appointment_reminder",
    "absence_recorded",
    "holiday_added",
    "student_struggling",
    "join_request",
    "deletion_request",
    "attendance_review",
    "wird_reminder",
    "teacher_available",
    "availability_check",
]);
export const NotificationTarget = z.object({
    result: z.uuid().optional(),
    session: z.uuid().optional(),
    exam: z.uuid().optional(),
    plan: z.uuid().optional(),
    /** The student a teacher or supervisor should look at (struggling, attendance review). */
    student: z.uuid().optional(),
    joinRequest: z.uuid().optional(),
    deletionRequest: z.uuid().optional(),
    /** The study day it is about (absence, holiday, wird reminder), YYYY-MM-DD. */
    date: z.string().optional(),
    /** A teacher's «أنا متاح الآن» (the student's banner, the teacher's «هل ما زلت متاحًا؟»). */
    availability: z.uuid().optional(),
    /** Requests gathered in one notification (at most one every ten minutes). */
    count: z.number().optional(),
});
export const AppNotification = z.object({
    id: z.uuid(),
    kind: NotificationKind,
    title: z.string(),
    body: z.string().nullable(),
    target: NotificationTarget,
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
export const NotificationsResponse = z.object({
    notifications: z.array(AppNotification),
    unread: z.number(),
});
/** Marks the given notifications read, or all of the caller's when ids is omitted. */
export const MarkNotificationsReadBody = z.object({ ids: z.array(z.uuid()).max(200).optional() });
/** Registers this device for push notifications (an FCM registration token). */
export const PushTokenBody = z.object({
    token: z.string().min(20).max(4096),
    platform: z.enum(["ios", "android"]),
});
export const RemovePushTokenBody = z.object({ token: z.string().min(1).max(4096) });
/**
 * A message the organization's administration writes in the portal and sends to chosen people. It arrives
 * like any other notification (in the app and as a push). The text is the sender's own.
 */
export const SendAnnouncementBody = z.object({
    organizationId: z.uuid(),
    userIds: z.array(z.uuid()).min(1).max(500),
    title: z.string().trim().min(1).max(80),
    body: z.string().trim().max(500).optional(),
    /** Where it goes (owner request 2026-10-22): the app (in-app and push), WhatsApp, or both. */
    channels: z
        .array(z.enum(["app", "whatsapp"]))
        .min(1, "اختر طريقة الإرسال")
        .max(2)
        .default(["app"]),
});
export const SendAnnouncementResponse = z.object({
    /** Recipients who got it in the app (0 when the app was not chosen). */
    sent: z.number(),
    /** WhatsApp messages queued: they go out one by one through the organization's gateway after the reply. */
    whatsapp: z.number(),
    /** Chosen recipients without a phone number, so without the WhatsApp message. */
    noPhone: z.number(),
});
/** A time of day, "HH:MM" (24 hours). */
export const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "اكتب الوقت بصيغة 14:30");
/**
 * The student's daily wird reminder (owner decision 2026-10-19). By default it comes half an hour before their
 * circle's study time on days with portions still to recite; they may turn it off or pick their own time.
 */
export const ReminderResponse = z.object({
    enabled: z.boolean(),
    /** The student's own time, or null for the circle's default. */
    time: TimeOfDay.nullable(),
    /** Half an hour before the circle's study time, when the circle has one. */
    circleDefault: TimeOfDay.nullable(),
});
export const ReminderBody = z.object({ enabled: z.boolean(), time: TimeOfDay.nullable() });
//# sourceMappingURL=notifications.js.map